import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { AlertTriangle, X } from "lucide-react";
import { useLocation } from "wouter";

type ImpersonationStatus = {
  active: boolean;
  expired?: boolean;
  targetUser?: { id: number; email: string; name: string } | null;
  expiresAt?: number;
  sessionId?: number;
};

export function ImpersonationBanner() {
  const [, navigate] = useLocation();

  const statusQuery = useQuery<ImpersonationStatus>({
    queryKey: ["/api/app/impersonation/status"],
    queryFn: async () => {
      const res = await fetch("/api/app/impersonation/status", { credentials: "include" });
      if (!res.ok) return { active: false };
      return res.json();
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const endMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      await apiRequest("POST", "/api/app/impersonation/end");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/impersonation/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      queryClient.clear();
      navigate("/app/dashboard");
    },
  });

  const status = statusQuery.data;
  if (!status?.active || !status.targetUser) return null;

  const { targetUser, expiresAt } = status;
  const minutesLeft = expiresAt ? Math.max(0, Math.floor((expiresAt - Date.now()) / 60000)) : 0;

  return (
    <div
      className="w-full bg-amber-500 text-white px-4 py-2 flex items-center gap-3 text-sm font-medium z-50"
      data-testid="banner-impersonation"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        ⚠ Impersonating <strong>{targetUser.name || targetUser.email}</strong> ({targetUser.email})
        {minutesLeft > 0 && <span className="font-normal text-amber-100 ml-2">· {minutesLeft}m remaining</span>}
      </span>
      <button
        onClick={() => endMutation.mutate()}
        disabled={endMutation.isPending}
        className="flex items-center gap-1.5 bg-white text-amber-700 px-3 py-1 rounded text-xs font-semibold hover:bg-amber-50 transition-colors"
        data-testid="button-exit-impersonation"
      >
        <X className="h-3 w-3" />
        Exit Impersonation
      </button>
    </div>
  );
}
